/**
 * The sending domain's reputation: the one number that decides whether anybody
 * should be sending at all.
 *
 * THE DOMAIN, NOT A CAMPAIGN. Reputation is carried by the domain, so three
 * well-behaved campaigns do not protect the fourth - and on an overview screen
 * the domain is the only scope that means anything anyway.
 *
 * THE SAME SEVEN DAYS AS THE SENDER. The rates come from `checkDomainBreaker`,
 * which is `evaluateBreaker` with no campaign in the question, so this card and
 * the batch that refuses to run cannot reach different conclusions. A dashboard
 * with its own idea of the bounce rate is a dashboard that shows green while
 * the sender is stopped.
 *
 * READ ONLY. Waiving a trip is done on the campaign that tripped it, where the
 * operator can see what they are waiving. There is no button here that makes a
 * limit go away.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, Gauge, ShieldAlert, ShieldCheck } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { DomainHealth } from "../../../../lib/marketingOverview";
import { rateText } from "../lib";
import { Note, Panel, dt } from "../shared";
import { Scope } from "../panels/BreakerScope";

export function HealthCard({ health }: { health: DomainHealth }) {
  const t = useT();
  // Three states, and waived must not look clean: an acknowledgement is
  // somebody's decision to keep sending past a limit, and it expires.
  const state = health.trip ? "tripped" : health.waived ? "waived" : "ok";
  const icon =
    state === "tripped" ? <ShieldAlert className="size-4" />
    : state === "waived" ? <Gauge className="size-4" />
    : <ShieldCheck className="size-4" />;

  return (
    <Panel
      title={t("marketing.overview.health.title")}
      description={t("marketing.overview.health.intro", { days: health.windowDays })}
      icon={icon}
      actions={
        <Link to="/marketing/campaigns" className="crm-btn">
          {t("nav.campaigns")}
          <ChevronRight className="size-4" />
        </Link>
      }
    >
      {health.trip ? (
        <Note tone="bad" icon={<AlertTriangle className="size-4" />} className="mb-4">
          {t(`marketing.breaker.tripped.${health.trip.reason}`, {
            scope: t(`marketing.breaker.scope.${health.trip.scope}`),
            rate: rateText(health.trip.rate),
            limit: rateText(health.trip.limit),
          })}
        </Note>
      ) : null}

      {health.ack ? (
        <Note tone="warn" className="mb-4">
          {t(health.waived ? "marketing.breaker.waived" : "marketing.breaker.waiverOutgrown", {
            until: dt(health.ack.until),
            bounce: rateText(health.ack.bounceRate),
            complaint: rateText(health.ack.complaintRate),
          })}
        </Note>
      ) : null}

      <Scope
        title={t("marketing.breaker.scope.domain")}
        rates={health.rates}
        windowDays={health.windowDays}
        limits={health.limits}
      />

      <p className="crm-sub mt-4">
        {t("marketing.overview.health.limits", {
          bounce: rateText(health.limits.bounce),
          complaint: rateText(health.limits.complaint),
        })}
      </p>
    </Panel>
  );
}
